<template>
  <div
    :class="[
      'sam-floating-actions__positioningHelper',
      { 'is-aligned-with-viewport': alignWithViewport },
    ]"
  >
    <transition name="fade">
      <aside
        v-if="persistent || hasValue"
        data-samora
        :class="[
          'sam-floating-actions',
          { 'is-persistent': persistent },
          { 'is-visible': hasValue },
        ]"
      >
        <slot v-if="$slots.default" />
        <template v-else>
          <div class="sam-floating-actions__messageWrapper">
            <SamButton
              v-if="clearable && hasValue"
              class="sam-floating-actions__clearButton"
              icon="interface-close"
              :sam-type="BUTTON_TYPES.TEXT"
              :sam-style="BUTTON_STYLES.INHERITED"
              @click="handleClear"
            />
            <span class="text--body-2--medium">
              <slot
                v-if="$slots.message"
                name="message"
              />
              <template v-else-if="message">
                {{ message }}
              </template>
              <template v-else>
                {{ fallbackMessage }}
              </template>
            </span>
          </div>
          <div class="sam-floating-actions__actionsWrapper">
            <slot
              v-if="$slots.actions"
              name="actions"
            />
            <template v-else>
              <SamButton
                v-for="(action, index) in normalizedActions"
                :key="`action__${action.id || index}`"
                v-bind="action"
                @click="handleActionOnClick($event, action, index)"
              />
            </template>
          </div>
        </template>
      </aside>
    </transition>
  </div>
</template>

<script>
import SamButton, { BUTTON_TYPES, BUTTON_STYLES } from '../SamButton/SamButton.vue';

const BUTTON_PROPS = [...Object.keys(SamButton.props), 'onClick'];

export default {
  components: { SamButton },
  props: {
    value: {
      type: null,
      required: true,
      default: () => [],
    },
    message: {
      type: String,
      required: false,
      default: null,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    persistent: {
      type: Boolean,
      required: false,
      default: false,
    },
    actions: {
      type: Array,
      required: false,
      default: () => [],
    },
    alignWithViewport: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    BUTTON_TYPES,
    BUTTON_STYLES,
  }),
  computed: {
    fallbackMessage() {
      return this.returnByValueType(
        `${this.value.length === 0 ? 'No' : this.value.length} item${
          this.value.length === 1 ? '' : 's'
        } selected`,
        this.value.name ?? this.value.id ?? 'Item',
        this.value
      );
    },
    normalizedActions() {
      return this.actions.map((action) => {
        return BUTTON_PROPS.reduce(
          (obj, prop) => {
            // eslint-disable-next-line no-param-reassign, eqeqeq
            if (action[prop] != null) obj[prop] = action[prop];
            return obj;
          },
          {
            samStyle: BUTTON_STYLES.LIGHT,
          }
        );
      });
    },
    hasValue() {
      // eslint-disable-next-line eqeqeq
      if (this.value == null) return false;
      return this.returnByValueType(this.value?.length > 0, true, !!this.value);
    },
  },
  methods: {
    returnByValueType(asArray, asObject, asFallback = null) {
      if (Array.isArray(this.value)) {
        return asArray;
      }
      if (typeof this.value === 'object') {
        return asObject;
      }
      return asFallback;
    },
    handleClear($event) {
      this.$emit('input', this.returnByValueType([], null));
      this.$emit('clear', $event);
    },
    handleActionOnClick($event, action, index) {
      const payload = { $event, $actionIndex: index, action };
      if (typeof action.onClick === 'function') action.onClick(payload);
      this.$emit('action-click', payload);
    },
  },
};
</script>

<style scoped lang="scss">
.sam-floating-actions {
  @extend .px-4, .py-2, .d-inline-flex, .gap-6, .elevation--1;
  background: $primary--black-rock;
  border-radius: $size--1;
  color: $pure--white;
  position: fixed;
  bottom: $size--8;
  scale: 1;
  transform-origin: center;

  &__clearButton.sam-button::v-deep {
    padding: 0 !important;
  }

  &__messageWrapper {
    @extend .d-flex, .gap-1, .items-center;
  }

  &__actionsWrapper {
    @extend .d-flex, .items-center, .gap-2;
  }

  &__positioningHelper {
    height: 0;

    &:not(.is-aligned-with-viewport) {
      @extend .d-flex, .justify-center;
    }

    &.is-aligned-with-viewport .sam-floating-actions {
      left: 50%;
      translate: -50%;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  -webkit-transition: all 0.15s ease-in;
  -moz-transition: all 0.15s ease-in;
  -ms-transition: all 0.15s ease-in;
  -o-transition: all 0.15s ease-in;
  transition: all 0.15s ease-in;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
  transform: scale(0.6);
}
</style>
